import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Job,
  JobStats,
  CreateJob,
  UpdateJob,
  UpdateJobStatus,
  JobFilterParams
} from '@/lib/api/types'

export const jobService = {
  // Filter jobs with pagination
  filterJobs: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: JobFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Job>>> => {
    return await post<ApiResponse<PaginatedResponse<Job>>>(
      `jobs/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get job by ID (public endpoint)
  getJobById: async (jobId: string): Promise<ApiResponse<Job>> => {
    return await get<ApiResponse<Job>>(`jobs/private/${jobId}`)
  },

  // Get job by ID (admin endpoint)
  getJobByIdAdmin: async (jobId: string): Promise<ApiResponse<Job>> => {
    return await get<ApiResponse<Job>>(`jobs/admin/private/${jobId}`)
  },

  // Create a new job
  createJob: async (data: CreateJob): Promise<ApiResponse<Job>> => {
    return await post<ApiResponse<Job>>(
      'jobs/admin/private',
      data
    )
  },

  // Update an existing job
  updateJob: async (
    jobId: string,
    data: UpdateJob
  ): Promise<ApiResponse<Job>> => {
    return await put<ApiResponse<Job>>(
      `jobs/admin/private/${jobId}`,
      data
    )
  },

  // Update job status
  updateJobStatus: async (
    jobId: string,
    data: UpdateJobStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `jobs/admin/private/${jobId}/status`,
      data
    )
  },

  // Delete a job
  deleteJob: async (jobId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `jobs/admin/private/${jobId}`
    )
  },

  // Get published jobs count
  getPublishedJobsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('jobs/private/stats/published')
  },

  // Get draft jobs count
  getDraftJobsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('jobs/private/stats/draft')
  },

  // Search jobs
  searchJobs: async (
    searchBy?: string
  ): Promise<ApiResponse<Job[]>> => {
    const queryParams = new URLSearchParams()

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<Job[]>>(
      `jobs/private/search?${queryParams.toString()}`
    )
  },

  // Get job types
  getJobTypes: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'jobs/private/job-types'
    )
  },

  // Get job stats
  getJobStats: async (): Promise<ApiResponse<JobStats>> => {
    return await get<ApiResponse<JobStats>>('jobs/private/stats')
  }
}

